/**
 * The console and network trails a reported Issue carries (browser side).
 *
 * `installIssueDiagnostics()` runs once at startup (src/frontend.tsx). From then on it
 * keeps, in memory only:
 *   - the last 50 console errors and warnings (500 characters each);
 *   - the last 30 API calls and in-app navigations — method, path, status, duration.
 *     Never a request or response body, never a header.
 * Both are scrubbed of secrets (src/lib/redact.ts) as they are recorded, and again by the
 * server before storing. Nothing leaves the browser unless the user submits an Issue.
 */

import { redactAndCap } from "@/lib/redact";
import {
  CONSOLE_ENTRY_MAX_CHARS,
  CONSOLE_TRAIL_MAX,
  type ConsoleTrailEntry,
  NETWORK_ENTRY_MAX_CHARS,
  NETWORK_TRAIL_MAX,
  type NetworkTrailEntry,
} from "@/schemas/support-issues";

const consoleTrail: ConsoleTrailEntry[] = [];
const networkTrail: NetworkTrailEntry[] = [];
let installed = false;

function pushCapped<T>(list: T[], entry: T, max: number): void {
  list.push(entry);
  if (list.length > max) list.splice(0, list.length - max);
}

function describeArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

/** Path + query of a URL, relative to this origin when it is ours. */
function pathOf(input: string): string {
  try {
    const url = new URL(input, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}`
      : `${url.origin}${url.pathname}`;
  } catch {
    return input;
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function recordNavigation(): void {
  pushCapped(
    networkTrail,
    {
      kind: "navigation",
      path: redactAndCap(
        `${window.location.pathname}${window.location.search}`,
        NETWORK_ENTRY_MAX_CHARS,
      ),
      at: new Date().toISOString(),
    },
    NETWORK_TRAIL_MAX,
  );
}

export function installIssueDiagnostics(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  for (const level of ["error", "warn"] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      pushCapped(
        consoleTrail,
        {
          level,
          message: redactAndCap(args.map(describeArg).join(" "), CONSOLE_ENTRY_MAX_CHARS),
          at: new Date().toISOString(),
        },
        CONSOLE_TRAIL_MAX,
      );
      original(...args);
    };
  }

  window.addEventListener("error", (event) => {
    pushCapped(
      consoleTrail,
      {
        level: "error",
        message: redactAndCap(`Uncaught: ${event.message}`, CONSOLE_ENTRY_MAX_CHARS),
        at: new Date().toISOString(),
      },
      CONSOLE_TRAIL_MAX,
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    pushCapped(
      consoleTrail,
      {
        level: "error",
        message: redactAndCap(
          `Unhandled rejection: ${describeArg(event.reason)}`,
          CONSOLE_ENTRY_MAX_CHARS,
        ),
        at: new Date().toISOString(),
      },
      CONSOLE_TRAIL_MAX,
    );
  });

  const nativeFetch = window.fetch;
  const originalFetch = nativeFetch.bind(window);
  const recordingFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const path = redactAndCap(pathOf(requestUrl(input)), NETWORK_ENTRY_MAX_CHARS);
    try {
      const res = await originalFetch(input, init);
      pushCapped(
        networkTrail,
        {
          kind: "fetch",
          method,
          path,
          status: res.status,
          durationMs: Math.round(performance.now() - started),
          at: new Date().toISOString(),
        },
        NETWORK_TRAIL_MAX,
      );
      return res;
    } catch (err) {
      pushCapped(
        networkTrail,
        {
          kind: "fetch",
          method,
          path,
          durationMs: Math.round(performance.now() - started),
          at: new Date().toISOString(),
        },
        NETWORK_TRAIL_MAX,
      );
      throw err;
    }
  };
  window.fetch = Object.assign(recordingFetch, { preconnect: nativeFetch.preconnect });

  // In-app navigations (TanStack Router drives the History API).
  for (const name of ["pushState", "replaceState"] as const) {
    const original = window.history[name].bind(window.history);
    window.history[name] = (...args: Parameters<History["pushState"]>) => {
      original(...args);
      recordNavigation();
    };
  }
  window.addEventListener("popstate", recordNavigation);
  recordNavigation();
}

export interface IssueDiagnosticsSnapshot {
  context: {
    pageUrl: string;
    route: string;
    userAgent: string;
    viewport: string;
  };
  consoleTrail: ConsoleTrailEntry[];
  networkTrail: NetworkTrailEntry[];
}

/**
 * A screen's route with ids replaced, so the queue groups "the same screen" —
 * `/dashboard/plans/3f…/overview` → `/dashboard/plans/:id/overview`.
 */
export function cleanRoute(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
      /^\d+$/.test(seg)
        ? ":id"
        : seg,
    )
    .join("/");
}

/** Copy the trails as they stand now — taken when the report form opens. */
export function snapshotIssueDiagnostics(): IssueDiagnosticsSnapshot {
  return {
    context: {
      pageUrl: redactAndCap(window.location.href, 2000),
      route: cleanRoute(window.location.pathname),
      userAgent: navigator.userAgent.slice(0, 500),
      viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`,
    },
    consoleTrail: [...consoleTrail],
    networkTrail: [...networkTrail],
  };
}
